// frontend/src/components/AdminIncompletePlayers.js
// Event-wide view of players still missing a photo, across every team.
// Lets an admin upload (with a forced square crop) for any incomplete player
// without first drilling into each team's detail page.
import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { supabase } from '../services/supabaseClient';
import toast, { Toaster } from 'react-hot-toast';
import AdminHeader from './AdminHeader';
import FlagDisplay from './FlagDisplay';
import PhotoCropModal from './PhotoCropModal';

const AdminIncompletePlayers = ({ eventCode }) => {
  const currentEventCode = eventCode || 'mjwc2025';
  const navigate = useNavigate();

  const [eventDetails, setEventDetails] = useState(null);
  const [players, setPlayers] = useState([]); // incomplete players only
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');

  // cropData: { player, imageUrl } — drives the shared CropModal
  const [cropData, setCropData] = useState(null);

  const fetchIncomplete = useCallback(async () => {
    setLoading(true);
    try {
      // 1. Resolve event to its DB short_name (the event_uploads partition key)
      const { data: event, error: eventError } = await supabase
        .from('events')
        .select('*')
        .eq('code', currentEventCode)
        .single();

      if (eventError || !event) throw new Error('Event not found');
      setEventDetails(event);

      // 2. Fetch players for this event that still have no photo
      //    (upload_status is null or false).
      const { data, error } = await supabase
        .from('event_uploads')
        .select('id, team, event_name, player_name, photo_url, upload_status, updated_at')
        .eq('event_name', event.short_name)
        .or('upload_status.is.null,upload_status.eq.false')
        .order('team', { ascending: true })
        .order('player_name', { ascending: true });

      if (error) throw error;

      // Guard client-side too, in case a row has upload_status true but no photo.
      setPlayers((data || []).filter((p) => !p.upload_status || !p.photo_url));
    } catch (error) {
      console.error('Error fetching incomplete players:', error);
      toast.error('Failed to load incomplete players: ' + error.message);
    } finally {
      setLoading(false);
    }
  }, [currentEventCode]);

  useEffect(() => {
    fetchIncomplete();
  }, [fetchIncomplete]);

  // Read the chosen file into a data URL and open the crop modal.
  const handleSelectFile = (event, player) => {
    const file = event.target.files[0];
    event.target.value = null; // allow re-selecting the same file later
    if (!file) return;

    if (!file.type.startsWith('image/')) {
      toast.error('Please choose an image file');
      return;
    }

    const reader = new FileReader();
    reader.onload = () => setCropData({ player, imageUrl: reader.result, file });
    reader.readAsDataURL(file);
  };

  // Shared upload: store the blob and mark the player complete.
  // Used by both "Apply Crop" (cropped square) and "Upload as is" (original file).
  const uploadAndComplete = async (player, blob) => {
    if (!player || !blob) return;

    const uploadToast = toast.loading(`Uploading photo for ${player.player_name}...`);
    try {
      // Storage path mirrors the team-detail upload convention:
      // <eventCode>/<team>/<player>_<ts>.jpg
      const timestamp = Date.now();
      const fileName = `${currentEventCode}/${player.team}/${player.player_name.replace(/\s+/g, '_')}_${timestamp}.jpg`;

      const { error: uploadError } = await supabase.storage
        .from('player-photos')
        .upload(fileName, blob, {
          contentType: 'image/jpeg',
          upsert: true
        });

      if (uploadError) throw uploadError;

      const { data: { publicUrl } } = supabase.storage
        .from('player-photos')
        .getPublicUrl(fileName);

      const { error: dbError } = await supabase
        .from('event_uploads')
        .update({
          upload_status: true,
          photo_url: publicUrl,
          uploaded_photo_url: publicUrl, // backup of the upload, matches team-detail behavior
          updated_at: new Date().toISOString()
        })
        .eq('id', player.id);

      if (dbError) throw dbError;

      // Optimistically drop the now-complete player from the list.
      setPlayers((prev) => prev.filter((p) => p.id !== player.id));
      toast.success(`Photo added for ${player.player_name}`, { id: uploadToast });
    } catch (error) {
      console.error('Upload error:', error);
      toast.error('Failed to upload photo: ' + error.message, { id: uploadToast });
    }
  };

  // "Apply Crop" — upload the cropped square blob.
  const handleCropConfirm = (croppedBlob) => {
    const player = cropData?.player;
    setCropData(null);
    uploadAndComplete(player, croppedBlob);
  };

  // "Upload as is" — upload the originally selected file, no cropping.
  const handleUseOriginal = () => {
    const { player, file } = cropData || {};
    setCropData(null);
    uploadAndComplete(player, file);
  };

  // Filter by search, then group by team.
  const filteredPlayers = players.filter((p) =>
    p.player_name.toLowerCase().includes(searchQuery.toLowerCase()) ||
    (p.team || '').toLowerCase().includes(searchQuery.toLowerCase())
  );

  const groupedByTeam = filteredPlayers.reduce((acc, player) => {
    const key = player.team || 'Unassigned';
    if (!acc[key]) acc[key] = [];
    acc[key].push(player);
    return acc;
  }, {});

  const teamNames = Object.keys(groupedByTeam).sort((a, b) => a.localeCompare(b));

  return (
    <div className="min-h-screen bg-gray-50">
      <Toaster position="top-center" />
      <AdminHeader currentPage="events" />

      {/* Hero / header band */}
      <div className="relative overflow-hidden bg-mjwc-primary">
        <div className="absolute inset-0 z-0 bg-gradient-to-br from-mjwc-primary via-mjwc-secondary to-indigo-900" />
        <div className="relative z-20 px-6 py-8 max-w-7xl mx-auto">
          {/* Breadcrumb */}
          <div className="flex items-center gap-2 text-xs font-black uppercase tracking-widest text-white/70 mb-4">
            <button onClick={() => navigate('/admin/events')} className="hover:text-white transition-colors">
              Events
            </button>
            <svg className="w-3 h-3 opacity-40" fill="currentColor" viewBox="0 0 20 20">
              <path fillRule="evenodd" d="M7.293 14.707a1 1 0 010-1.414L10.586 10 7.293 6.707a1 1 0 011.414-1.414l4 4a1 1 0 010 1.414l-4 4a1 1 0 01-1.414 0z" clipRule="evenodd" />
            </svg>
            <button onClick={() => navigate(`/admin/${currentEventCode}`)} className="hover:text-white transition-colors">
              {eventDetails?.short_name || currentEventCode}
            </button>
            <svg className="w-3 h-3 opacity-40" fill="currentColor" viewBox="0 0 20 20">
              <path fillRule="evenodd" d="M7.293 14.707a1 1 0 010-1.414L10.586 10 7.293 6.707a1 1 0 011.414-1.414l4 4a1 1 0 010 1.414l-4 4a1 1 0 01-1.414 0z" clipRule="evenodd" />
            </svg>
            <span className="text-white">Incomplete Players</span>
          </div>

          <h1 className="text-3xl sm:text-4xl font-black text-white tracking-tight leading-none mb-2">
            Incomplete Players
          </h1>
          <p className="text-white/70 font-medium">
            {loading
              ? 'Loading…'
              : players.length === 0
                ? 'Every player in this event has a photo.'
                : `${players.length} player${players.length === 1 ? '' : 's'} across ${Object.keys(players.reduce((a, p) => ({ ...a, [p.team || 'Unassigned']: 1 }), {})).length} team(s) still need a photo.`}
          </p>
        </div>
      </div>

      {/* Controls */}
      <div className="px-4 py-4 bg-white border-b">
        <div className="max-w-7xl mx-auto flex gap-3 flex-wrap items-center">
          <div className="relative flex-1 min-w-[220px]">
            <input
              type="text"
              placeholder="Search by player or team…"
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="w-full px-10 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-mjwc-primary"
            />
            <svg className="absolute left-3 top-2.5 h-5 w-5 text-gray-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
            </svg>
          </div>
          <button
            onClick={fetchIncomplete}
            className="px-4 py-2 bg-gray-100 text-gray-700 rounded-lg hover:bg-gray-200 transition-colors flex items-center gap-2"
          >
            <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
            </svg>
            Refresh
          </button>
        </div>
      </div>

      {/* Body */}
      <div className="px-4 py-6 max-w-7xl mx-auto">
        {loading ? (
          <div className="flex justify-center py-16">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-mjwc-primary"></div>
          </div>
        ) : players.length === 0 ? (
          <div className="text-center py-20">
            <div className="text-5xl mb-4">🎉</div>
            <h2 className="text-xl font-bold text-gray-800 mb-1">All caught up!</h2>
            <p className="text-gray-500">Every player in this event already has a photo.</p>
            <button
              onClick={() => navigate(`/admin/${currentEventCode}`)}
              className="mt-6 px-5 py-2.5 bg-mjwc-primary text-white rounded-lg hover:bg-mjwc-secondary transition-colors font-semibold"
            >
              Back to Dashboard
            </button>
          </div>
        ) : teamNames.length === 0 ? (
          <div className="text-center py-16 text-gray-500">No players match your search.</div>
        ) : (
          <div className="space-y-8">
            {teamNames.map((teamName) => (
              <section key={teamName}>
                {/* Team header */}
                <div className="flex items-center gap-3 mb-3">
                  <FlagDisplay teamName={teamName} size="md" />
                  <h2 className="text-lg font-bold text-gray-900">{teamName}</h2>
                  <span className="text-xs font-bold uppercase tracking-wider text-amber-700 bg-amber-100 px-2 py-0.5 rounded-full">
                    {groupedByTeam[teamName].length} missing
                  </span>
                  <button
                    onClick={() => navigate(`/admin/${currentEventCode}/${encodeURIComponent(teamName)}`)}
                    className="ml-auto text-sm text-mjwc-primary hover:underline font-semibold"
                  >
                    Open team →
                  </button>
                </div>

                {/* Player rows */}
                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
                  {groupedByTeam[teamName].map((player) => (
                    <div
                      key={player.id}
                      className="flex items-center gap-3 bg-white rounded-lg border border-gray-200 p-3 hover:border-mjwc-primary hover:shadow-sm transition-all"
                    >
                      <div className="w-10 h-10 rounded-full bg-gray-100 flex items-center justify-center text-gray-400 shrink-0">
                        <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" />
                        </svg>
                      </div>
                      <p className="font-medium text-gray-900 truncate flex-1" title={player.player_name}>
                        {player.player_name}
                      </p>
                      <label
                        className="shrink-0 inline-flex items-center gap-1.5 px-3 py-1.5 bg-green-600 text-white text-sm font-semibold rounded-lg hover:bg-green-700 transition-colors cursor-pointer active:scale-95"
                        title="Upload photo (square crop)"
                      >
                        <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M7 16a4 4 0 01-.88-7.903A5 5 0 1115.9 6L16 6a5 5 0 011 9.9M15 13l-3-3m0 0l-3 3m3-3v12" />
                        </svg>
                        Upload
                        <input
                          type="file"
                          accept="image/*"
                          className="hidden"
                          onChange={(e) => handleSelectFile(e, player)}
                        />
                      </label>
                    </div>
                  ))}
                </div>
              </section>
            ))}
          </div>
        )}
      </div>

      {/* Shared crop modal (react-image-crop, locked 1:1 square).
          Admin gets an X to dismiss, "Upload as is" to skip cropping, and a "Cancel" label. */}
      <PhotoCropModal
        show={!!cropData}
        imageUrl={cropData?.imageUrl}
        onConfirm={handleCropConfirm}
        onCancel={() => setCropData(null)}
        onClose={() => setCropData(null)}
        onUseOriginal={handleUseOriginal}
        cancelText="Cancel"
        confirmText="Crop & Save"
        freeCrop
        title="Crop Photo"
        subtitle={cropData?.player ? `${cropData.player.player_name} — drag edges or corners to frame` : undefined}
      />
    </div>
  );
};

export default AdminIncompletePlayers;
